export enum PaperKind {
  Blank = 'blank',
  Grid = 'grid',
  Lined = 'lined',
  Dots = 'dots',
  Photo = 'photo',
  Template = 'template',
}

export enum PageSizeId {
  Letter = 'letter',
  A4 = 'a4',
  Wide = 'wide',
}

export interface PageDimensions {
  readonly width: number;
  readonly height: number;
}

export interface PageSizeOption {
  readonly id: PageSizeId;
  readonly label: string;
  readonly portrait: PageDimensions;
}

export const PAGE_SIZES: readonly PageSizeOption[] = [
  { id: PageSizeId.Letter, label: 'Letter', portrait: { width: 816, height: 1056 } },
  { id: PageSizeId.A4, label: 'A4', portrait: { width: 794, height: 1123 } },
  { id: PageSizeId.Wide, label: 'Wide', portrait: { width: 900, height: 1400 } },
];

export const PAPER_OPTIONS: readonly { kind: PaperKind; label: string }[] = [
  { kind: PaperKind.Blank, label: 'Blank' },
  { kind: PaperKind.Grid, label: 'Graph paper' },
  { kind: PaperKind.Lined, label: 'Lined' },
  { kind: PaperKind.Dots, label: 'Dot grid' },
];

export const PAPER_LABELS: Readonly<Record<PaperKind, string>> = {
  [PaperKind.Blank]: 'Blank',
  [PaperKind.Grid]: 'Graph paper, 1/4 inch',
  [PaperKind.Lined]: 'Lined',
  [PaperKind.Dots]: 'Dot grid, 1/4 inch',
  [PaperKind.Photo]: 'Photo',
  [PaperKind.Template]: 'Template',
};

export const GRID_CELL_PX = 24;
export const MAJOR_LINE_EVERY = 4;
export const LINE_SPACING_PX = 28;
export const DOT_SPACING_PX = 24;
export const BLANK_TILE_PX = 8;
export const LINED_MARGIN_PX = 72;
export const PAPER_WHITE = '#FFFFFF';
export const GRID_MINOR_COLOR = '#D5E3F5';
export const GRID_MAJOR_COLOR = '#B3C9E8';
export const LINE_COLOR = '#C7D6EA';
export const MARGIN_COLOR = '#F2B8B8';
export const DOT_COLOR = '#B9C7DB';
export const DOT_RADIUS_PX = 1.2;
export const MAX_PHOTO_PAGE_EDGE_PX = 1600;
export const MIN_PHOTO_PAGE_EDGE_PX = 200;

export interface PaperPaint {
  fillStyle: string | CanvasGradient | CanvasPattern;
  strokeStyle: string | CanvasGradient | CanvasPattern;
  lineWidth: number;
  fillRect(x: number, y: number, w: number, h: number): void;
  beginPath(): void;
  moveTo(x: number, y: number): void;
  lineTo(x: number, y: number): void;
  stroke(): void;
  arc(x: number, y: number, radius: number, startAngle: number, endAngle: number): void;
  fill(): void;
}

export function tileSizeFor(kind: PaperKind): number {
  switch (kind) {
    case PaperKind.Grid:
      return GRID_CELL_PX * MAJOR_LINE_EVERY;
    case PaperKind.Lined:
      return LINE_SPACING_PX;
    case PaperKind.Dots:
      return DOT_SPACING_PX;
    default:
      return BLANK_TILE_PX;
  }
}

function crispLine(ctx: PaperPaint, x1: number, y1: number, x2: number, y2: number): void {
  ctx.beginPath();
  ctx.moveTo(x1, y1);
  ctx.lineTo(x2, y2);
  ctx.stroke();
}

export function paintPaperTile(ctx: PaperPaint, kind: PaperKind): number {
  const size = tileSizeFor(kind);
  ctx.fillStyle = PAPER_WHITE;
  ctx.fillRect(0, 0, size, size);
  ctx.lineWidth = 1;

  if (kind === PaperKind.Grid) {
    for (let i = 1; i < MAJOR_LINE_EVERY; i++) {
      const pos = i * GRID_CELL_PX + 0.5;
      ctx.strokeStyle = GRID_MINOR_COLOR;
      crispLine(ctx, pos, 0, pos, size);
      crispLine(ctx, 0, pos, size, pos);
    }
    ctx.strokeStyle = GRID_MAJOR_COLOR;
    crispLine(ctx, 0.5, 0, 0.5, size);
    crispLine(ctx, 0, 0.5, size, 0.5);
  } else if (kind === PaperKind.Lined) {
    ctx.strokeStyle = LINE_COLOR;
    crispLine(ctx, 0, size - 0.5, size, size - 0.5);
  } else if (kind === PaperKind.Dots) {
    ctx.fillStyle = DOT_COLOR;
    ctx.beginPath();
    ctx.arc(DOT_SPACING_PX / 2, DOT_SPACING_PX / 2, DOT_RADIUS_PX, 0, Math.PI * 2);
    ctx.fill();
  }
  return size;
}

export function pageDimensions(sizeId: PageSizeId, landscape: boolean): PageDimensions {
  const option = PAGE_SIZES.find((s) => s.id === sizeId) || PAGE_SIZES[0];
  return landscape ? { width: option.portrait.height, height: option.portrait.width } : option.portrait;
}

export function fitPhotoPage(imageWidth: number, imageHeight: number): PageDimensions & { scale: number } {
  const longest = Math.max(imageWidth, imageHeight);
  if (!Number.isFinite(longest) || longest <= 0) {
    return { width: MIN_PHOTO_PAGE_EDGE_PX, height: MIN_PHOTO_PAGE_EDGE_PX, scale: 1 };
  }
  const scale = Math.min(1, MAX_PHOTO_PAGE_EDGE_PX / longest);
  return {
    width: Math.max(MIN_PHOTO_PAGE_EDGE_PX, Math.round(imageWidth * scale)),
    height: Math.max(MIN_PHOTO_PAGE_EDGE_PX, Math.round(imageHeight * scale)),
    scale,
  };
}

export function snapToGrid(value: number, cell: number): number {
  return Math.round(value / cell) * cell;
}

export function fitZoom(viewportWidth: number, viewportHeight: number, page: PageDimensions, margin: number): number {
  const availableWidth = Math.max(1, viewportWidth - margin * 2);
  const availableHeight = Math.max(1, viewportHeight - margin * 2);
  return Math.min(availableWidth / page.width, availableHeight / page.height);
}

export function centeredViewport(viewportWidth: number, viewportHeight: number, page: PageDimensions, zoom: number): [number, number, number, number, number, number] {
  const offsetX = (viewportWidth - page.width * zoom) / 2;
  const offsetY = (viewportHeight - page.height * zoom) / 2;
  return [zoom, 0, 0, zoom, offsetX, offsetY];
}
